/**
 * BulkAddCourseSectionManager — API layer for the Bulk Add Course Section feature.
 *
 * This file centralises every HTTP call the feature makes. It exists so that
 * UI components never construct URLs or manage request headers directly.
 *
 * Key design decisions:
 *
 * 1. **Two section-list endpoints** — `getSectionList` (legacy JSON) and
 *    `getSectionListCDG` (CDG format).  The CDG variant is used by the Bulk
 *    Add modal because CDG responses carry server-side pagination metadata
 *    (`pagination.totalResults`, `numPages`) and field-level permissions,
 *    which the DataGrid relies on to render correctly without fetching the
 *    entire dataset up-front.
 *
 * 2. **CDG content negotiation** — `getSectionListCDG` sets the `Accept`
 *    header to `application/cdg`.  The backend inspects this header and
 *    returns the CDG-shaped response (rows wrapped in `{ actions, data,
 *    permissions, views }` objects) instead of flat JSON.
 *
 * 3. **Background task pattern** — Bulk-adding sections is a long-running
 *    server-side operation.  `createBulkAddCourseSection` triggers the task
 *    and returns a `taskId`; `getTaskStatus` polls that ID so the UI can
 *    show progress or completion status.
 *
 * 4. **Filter header metadata** — `fetchCourseSectionHeaders` returns the
 *    list of filterable fields along with their data types (TEXT, NUMBER,
 *    DATE, BOOLEAN).  The filter panel uses this to offer type-aware
 *    operators (e.g. "Before / After" for dates, "Contains" for strings).
 */
import { type AxiosResponse } from 'axios'
import api from '../utils/api'
import { createSectionFilterPayload } from '../helpers/BulkAddCourseSection'

/**
 * Fetches CDG header metadata that describes every filterable field
 * (field name, display header, data type).  The filter panel uses the
 * `type` property to decide which operators are valid for a given field.
 * Cached for 5 minutes at the query layer since headers rarely change.
 */
export const fetchCourseSectionHeaders = async (): Promise<any[]> => {
  const { data } = await api.get('/api/sections/cdg_headers')
  return data
}

/**
 * Legacy (non-CDG) section list — returns flat JSON rows.
 * Kept for backwards compatibility with other consumers; the Bulk Add
 * modal no longer uses this endpoint because it lacks server-side
 * pagination metadata.
 */
export const getSectionList = async (filters: any[] = [], termId?: string, slotId?: string): Promise<AxiosResponse> => {
  const params: any = {
    term_id: termId,
    slot_id: slotId
  }

  if (filters && filters.length > 0) {
    params.filters = JSON.stringify(filters)
  }

  return await api.get('/api/sections/', { params })
}

/**
 * CDG-based section list — the primary data source for the Bulk Add
 * DataGrid.  Differences from the legacy endpoint:
 *
 * - Sends `Accept: application/cdg` so the backend returns CDG-shaped
 *   rows (`{ actions, data, dataPermissions, permissions, views }`).
 * - Accepts `pageNumber` / `pageSize` for server-side pagination;
 *   the response includes a `pagination` object used by the custom
 *   footer to render page controls.
 * - Accepts a `search` term that the backend applies across
 *   searchable fields (e.g. abbreviation, title).
 * - Filters are JSON-stringified so array structures survive the
 *   query-string encoding.
 */
export const getSectionListCDG = async (
  filters: any[] = [],
  termId?: string,
  slotId?: string,
  pageNumber: number = 1,
  pageSize: number = 7,
  search?: string,
  isPresent?: boolean
): Promise<AxiosResponse> => {
  const params: any = {
    term_id: termId,
    slot_id: slotId,
    pageNumber,
    pageSize
  }

  // Bulk Update mode lists only already-attached sections. Set the snake_case
  // key literally (like term_id/slot_id) to guarantee the exact `is_present`
  // query param regardless of case-conversion middleware.
  if (isPresent !== undefined) {
    params.is_present = isPresent
  }

  if (search) {
    params.search = search
  }

  if (filters && filters.length > 0) {
    params.filters = JSON.stringify(filters)
  }

  return await api.get('/api/sections/', {
    headers: { Accept: 'application/cdg' },
    params
  })
}

/** Slot-status views the section list supports; the two used here are the two bulk actions. */
export type SlotSectionView = 'closed' | 'published'

/**
 * The catalog filter fields that also exist on the slot sections list, and what each is called
 * there.
 *
 * The bulk-select filter panel is populated from `/api/sections/cdg_headers`, which describes the
 * **Section catalog** (`abbreviation`, `title`, `enrollment`, `cross_list_code`, …).
 * `GET /api/slots/{id}/sections/` is keyed on SlotNode columns and reaches Section fields through
 * `linked_section.*`, so those names do not line up. A filter field the endpoint does not know is
 * *silently dropped* server-side rather than rejected, so an untranslated filter does not fail —
 * it comes back unfiltered, which on these two surfaces means Select All would sweep the whole
 * term. Hence: translate what has an equivalent, and let the modal offer only these fields
 * (`toSlotSectionFilterRows`) so nothing an admin can pick is quietly ignored.
 *
 * `headerName` is overridden because on this endpoint the timestamps are the *survey* dates, not
 * the catalog's section dates, and the two must not read the same in the dropdown.
 *
 * Deliberately absent: `linked_section.enrollment` declares no `join_field`, so sending it 400s;
 * `title` and `cross_list_code` have no key on this endpoint at all. `is_opted_out` and
 * `section_id` are filterable (Backend #2130) but are not offered here either — neither is a
 * condition an admin writes. Both are appended by this module, which is what
 * `buildBulkSectionStatusFilters` is for.
 */
const SLOT_SECTION_FILTER_FIELDS: Record<string, { field: string, headerName: string }> = {
  abbreviation: { field: 'linked_section.abbreviation', headerName: 'Section ID' },
  start_timestamp: { field: 'start_timestamp', headerName: 'Survey Start Date' },
  end_timestamp: { field: 'end_timestamp', headerName: 'Survey End Date' }
}

/** Rewrites catalog filter fields to their slot-sections keys, dropping any with no equivalent. */
const toSlotSectionFilters = (filters: any[] = []): any[] =>
  filters
    .filter(filter => SLOT_SECTION_FILTER_FIELDS[filter?.field] !== undefined)
    .map(filter => ({ ...filter, field: SLOT_SECTION_FILTER_FIELDS[filter.field].field }))

/**
 * Drops the header rows the endpoint says it cannot filter on.
 *
 * `/api/sections/cdg_headers` returns every key, including ones whose `allow_filter` is false, so
 * the panel has to narrow them itself — the key map is the endpoint's statement of what may be
 * filtered, and this is the client reading it.
 *
 * Until now nothing read the flag: the panel offered whatever `FilterRow` had an input widget for
 * (TEXT / NUMBER / DATE / BOOLEAN), and the only non-filterable catalog key — `primary_instructors`
 * — is an `OBJECT_LIST`, so the type check excluded it by coincidence rather than on purpose. Any
 * future non-filterable key of a supported type would have been offered, and a filter on it is
 * *silently dropped* server-side: the list comes back unfiltered, which on the Select All path
 * means a bulk action over sections the admin never chose.
 *
 * Absent, rather than false, is treated as filterable: `Key.dump` always emits the flag, so a
 * missing one means a caller passed rows from somewhere else, and defaulting those to "cannot
 * filter" would empty the dropdown outright.
 */
export const toFilterableRows = (rows: any[] = []): any[] =>
  rows.filter(row => row?.allowFilter !== false)

/**
 * Narrows the catalog's CDG header metadata to the fields this endpoint can actually filter on,
 * relabeled for what they mean here. The filter panel uses it so the two bulk status actions never
 * offer a condition the backend would discard.
 */
export const toSlotSectionFilterRows = (rows: any[] = []): any[] =>
  rows
    .filter(row => SLOT_SECTION_FILTER_FIELDS[row?.field] !== undefined)
    .map(row => ({ ...row, headerName: SLOT_SECTION_FILTER_FIELDS[row.field].headerName }))

/**
 * Course-section surveys already attached to a Survey Project, narrowed to one slot status —
 * `closed` for Bulk Reopen, `published` for Bulk Go Live.
 *
 * A different endpoint from `getSectionListCDG` on purpose. That one lists the *Section catalog*
 * and can only say "attached or not" (`is_present`) — its filterable keys are Section columns,
 * with no survey status among them, so it cannot express "closed only". This one filters
 * SlotNodes by status via `view`. `is_present` is meaningless here: slot nodes are attached by
 * definition.
 *
 * Its filter keys are **not** the catalog's, though: this list is keyed on SlotNode columns and
 * reaches Section fields through `linked_section.*`, so a catalog field name has to be translated
 * before it is sent — see `SLOT_SECTION_FILTER_FIELDS`. Search needs no translation: the backend
 * builds it from this endpoint's own keys.
 *
 * Rows come back in SlotNode shape, so they are flattened to the shape the bulk-select table
 * already consumes — see `toBulkSelectRow`.
 */
export const getSlotSectionListCDG = async (
  slotId: string,
  view: SlotSectionView,
  filters: any[] = [],
  pageNumber: number = 1,
  pageSize: number = 7,
  search?: string,
  isOptOutSettingEnabled?: boolean
): Promise<AxiosResponse> => {
  const params: any = {
    view,
    pageNumber,
    pageSize
  }

  if (search) {
    params.search = search
  }

  const slotFilters = [
    ...toSlotSectionFilters(filters),
    ...optedOutExclusionFilters(isOptOutSettingEnabled)
  ]
  if (slotFilters.length > 0) {
    params.filters = JSON.stringify(slotFilters)
  }

  const response = await api.get(`/api/slots/${slotId}/sections/`, {
    headers: { Accept: 'application/cdg' },
    params
  })

  const rows: any[] = (response.data?.data ?? []).map(toBulkSelectRow)

  return {
    ...response,
    data: {
      ...response.data,
      data: rows
    }
  }
}

/**
 * Excludes opted-out course sections from a bulk status selection.
 *
 * A bulk status change should not offer sections it cannot act on: go-live refuses an opted-out
 * section outright ("cannot be set to Live because it has been opted out"), and reopening one
 * would revive a survey the Academic Unit or instructor had removed.
 *
 * Only sent when the project has opt-out switched on, which is the backend's own condition for
 * refusing the section (opted out **and** opt-out enabled). The endpoint cannot express that pair
 * in one filter — `is_opted_out` is a plain key and conditions are ANDed — but it does not need
 * to: `is_opt_out_setting_enabled` is a project-level property
 * (`is_academic_unit_opt_out_enabled | is_instructor_opt_out_enabled`), so whether the exclusion
 * applies at all is decided once, here, from the same two flags.
 *
 * Sent as a filter rather than dropped from the rows after they arrive, which is what this used to
 * do: `pagination.totalResults` counts what the query matched, so filtering client-side left the
 * matched total overstating what was selectable and pages rendering short. `is_opted_out` became
 * filterable in Backend #2130 for exactly this.
 */
const optedOutExclusionFilters = (isOptOutSettingEnabled?: boolean): any[] =>
  isOptOutSettingEnabled === true
    ? [{ field: 'is_opted_out', op: '==', value: false }]
    : []

/** Bulk Reopen's list: closed surveys only. */
export const getClosedSlotSectionListCDG = async (
  slotId: string,
  filters: any[] = [],
  pageNumber: number = 1,
  pageSize: number = 7,
  search?: string,
  isOptOutSettingEnabled?: boolean
): Promise<AxiosResponse> =>
  await getSlotSectionListCDG(slotId, 'closed', filters, pageNumber, pageSize, search, isOptOutSettingEnabled)

/** Bulk Go Live's list: published (upcoming) surveys only — the only status go-live accepts. */
export const getPublishedSlotSectionListCDG = async (
  slotId: string,
  filters: any[] = [],
  pageNumber: number = 1,
  pageSize: number = 7,
  search?: string,
  isOptOutSettingEnabled?: boolean
): Promise<AxiosResponse> =>
  await getSlotSectionListCDG(slotId, 'published', filters, pageNumber, pageSize, search, isOptOutSettingEnabled)

/**
 * Flattens a SlotNode CDG row into the shape the bulk-select table reads.
 *
 * `SlotNodeCDGSchema` carries no top-level `section_id`, `abbreviation` or `title` — they live on
 * the nested `linkedSection` — while the table derives row ids from `row.data.sectionId` and its
 * columns read `row.data.abbreviation` / `.title`. Flattening here rather than in the table keeps
 * the selection model, `getRowId` and the column definitions identical across both sources; the
 * alternative was threading an id accessor through four layers of selection logic.
 *
 * The start/end timestamps are left as the node's own: on this surface those are the *survey*
 * dates, which is what an admin reopening a survey wants to see, not the catalog dates.
 */
const toBulkSelectRow = (row: any): any => ({
  ...row,
  data: {
    ...row?.data,
    sectionId: row?.data?.linkedSection?.sectionId ?? row?.data?.sectionId,
    abbreviation: row?.data?.linkedSection?.abbreviation ?? row?.data?.abbreviation,
    title: row?.data?.linkedSection?.title ?? row?.data?.title
  }
})

/**
 * Submits a bulk-add request.  The backend processes this asynchronously
 * and returns a `taskId`.  The caller stores this ID in localStorage so
 * the modal can detect an in-progress task on subsequent opens and show
 * the TaskRunningModal instead of allowing a duplicate submission.
 */
export const createBulkAddCourseSection = async (slotId: string, data: any): Promise<AxiosResponse> => {
  return await api.post(`/api/slots/${slotId}/sections/bulk_add_sections`, data)
}

/**
 * The bulk-select modal's three-way selection, as the two status endpoints take it.
 *
 * `isOptOutSettingEnabled` is the project's own flag, not a row's: it decides whether opted-out
 * sections are excluded at all. It must carry the same value the selection list was read with, or
 * the batch resolves a different set of sections than the admin saw.
 */
export interface BulkSectionStatusSelection {
  /** Catalog-keyed filter conditions from the filter panel; translated before they are sent. */
  filters?: any[]
  search?: string
  selectAll: boolean
  selectedIds?: string[]
  /** Rows unticked while in Select All mode. */
  unselectedIds?: string[]
  isOptOutSettingEnabled?: boolean
}

/**
 * What a reopened survey becomes.
 *
 * `live` is the original behavior and the default: the survey opens the moment the batch commits.
 * `published` reopens it into the Upcoming state instead, to open on its own `startTimestamp` —
 * the same scheduled transition a normally-published section rides.
 */
export type ReopenTargetStatus = 'live' | 'published'

/** The shared reopen timestamps, already serialized as UTC instants by the confirm step. */
export interface BulkReopenTimestamps {
  endTimestamp: string
  earlyAccessReleaseTimestamp1?: string
  earlyAccessReleaseTimestamp2?: string
  /** Required for `published`, absent for `live` — a survey opening now has no start to set. */
  startTimestamp?: string
}

/**
 * The filter array that resolves a bulk status selection server-side.
 *
 * Three layers, in order: the admin's own conditions translated to this endpoint's keys, the
 * opted-out exclusion, and the selection itself as `section_id in` / `not_in`.
 *
 * The endpoint resolves these against the **same** query and key set the selection list used
 * (`build_slot_sections_query` + `_get_slot_section_keys`), with `view` fixed by the operation.
 * That is the reason the translation happens here too rather than being sent raw: `start_timestamp`
 * and `end_timestamp` exist in both the catalog and slot-sections key maps with *different*
 * meanings, so an untranslated filter would resolve against a different set of sections than the
 * one on screen and then change their status irreversibly (Backend #2130, Open Question 1).
 *
 * `section_id` is deliberately appended after translation rather than added to
 * `SLOT_SECTION_FILTER_FIELDS`: it is not a condition the filter panel should offer, and the
 * whitelist doubles as that panel's source.
 */
export const buildBulkSectionStatusFilters = (selection: BulkSectionStatusSelection): any[] =>
  createSectionFilterPayload(
    selection.selectAll,
    selection.unselectedIds ?? [],
    selection.selectedIds,
    [
      ...toSlotSectionFilters(selection.filters ?? []),
      ...optedOutExclusionFilters(selection.isOptOutSettingEnabled)
    ]
  )

/**
 * Reopens every closed course-section survey the selection resolves to, with one shared set of
 * dates. Async: answers `202` with a `taskId`, like `bulk_add_sections`.
 *
 * Replaced a frontend fan-out over `PATCH /slots/{slotId}/sections/{sectionId}/reopen/`. That loop
 * repeated four pieces of *per-project* work once per section — the division report index, the S3
 * export snapshot purge, the auto-close reschedule, and the round trip — and could not be
 * transactional. The server-side pass does each once and reports every section's outcome in the
 * task result.
 */
export const bulkReopenSlotSections = async (
  slotId: string,
  selection: BulkSectionStatusSelection,
  dates: BulkReopenTimestamps,
  targetStatus: ReopenTargetStatus = 'live'
): Promise<AxiosResponse> => {
  // `targetStatus` is sent **only** for `published`, and that is deliberate rather than tidy.
  // The endpoint does not know the field yet; omitting it on the live path means today's request
  // is byte-for-byte what it has always been, so nothing about the shipped behavior depends on how
  // the backend treats an unknown key. The published path is gated behind
  // `isBulkReopenScheduleEnabled` until the field exists, because a backend that *ignores* it
  // would reopen the batch Live while the admin was told it was scheduled — wrong in the one
  // direction that cannot be undone.
  const isScheduled = targetStatus === 'published'
  return await api.post(`/api/slots/${slotId}/sections/bulk_reopen`, {
    filters: buildBulkSectionStatusFilters(selection),
    ...(selection.search ? { search: selection.search } : {}),
    endTimestamp: dates.endTimestamp,
    ...(dates.earlyAccessReleaseTimestamp1 ? { earlyAccessReleaseTimestamp1: dates.earlyAccessReleaseTimestamp1 } : {}),
    ...(dates.earlyAccessReleaseTimestamp2 ? { earlyAccessReleaseTimestamp2: dates.earlyAccessReleaseTimestamp2 } : {}),
    ...(isScheduled ? { targetStatus, startTimestamp: dates.startTimestamp } : {})
  })
}

/**
 * Takes every published course-section survey the selection resolves to live. No dates: the
 * section already carries its own, and the endpoint takes only the selection.
 */
export const bulkGoLiveSlotSections = async (
  slotId: string,
  selection: BulkSectionStatusSelection
): Promise<AxiosResponse> => {
  return await api.post(`/api/slots/${slotId}/sections/bulk_go_live`, {
    filters: buildBulkSectionStatusFilters(selection),
    ...(selection.search ? { search: selection.search } : {})
  })
}

/**
 * Polls the status of an asynchronous bulk-add task.  Possible statuses
 * include SUCCESSFUL, COMPLETED, FAILED, REVOKED, ERROR, and in-progress
 * states.  The modal checks this on open to decide whether to allow a new
 * submission or to redirect the user to the Activity Monitor.
 */
export const getTaskStatus = async (taskId: string): Promise<AxiosResponse> => {
  return await api.get(`/api/tasks/${taskId}/`)
}

export default {
  fetchCourseSectionHeaders,
  getSectionList,
  getSectionListCDG,
  getSlotSectionListCDG,
  getClosedSlotSectionListCDG,
  getPublishedSlotSectionListCDG,
  toSlotSectionFilterRows,
  toFilterableRows,
  buildBulkSectionStatusFilters,
  bulkReopenSlotSections,
  bulkGoLiveSlotSections,
  getTaskStatus
}
